// WS listener das notificações. Espelha o estado de não-lidas do backend:
//   - `inbox/message.received` (INBOUND, conversa MINHA): seta o unreadCount absoluto
//     vindo do servidor; se a conversa está aberta na hora, dispara markRead (mantém 0).
//   - `inbox/conversation.read`: zera (sincroniza quando outro atendente lê).
//
// Setup: registrado uma única vez no boot via attachNotificationsWsListener().
import type { AnyAction } from '@reduxjs/toolkit';
import { listenerMiddleware } from '@/app/store/middleware';
import { selectCurrentUser } from '@/features/auth';
import { inboxApi, selectSelectedConversationId, type InboxMessage } from '@/features/inbox';
import { conversationUnreadSet } from './slice';

// Mesmo escape pragmático dos outros ws-listeners: o Dispatch genérico do
// listenerMiddleware não aceita os ThunkActions tipados de createApi sem ciclo.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LooseDispatch = (action: any) => any;

interface MessageReceivedPayload {
  conversationId: string;
  message: InboxMessage;
  /** unreadCount da conversa APÓS a mensagem (autoritativo do backend). */
  unreadCount: number;
  /** Dono da conversa (null = na fila). Usado pra filtrar "minhas". */
  assignedToId: string | null;
}

interface ConversationReadPayload {
  conversationId: string;
}

/**
 * Registra os listeners de não-lidas. Idempotente o suficiente — chamar 1× no boot.
 */
export function attachNotificationsWsListener(): void {
  // message.received → espelha o unreadCount da conversa (só minhas + cliente).
  listenerMiddleware.startListening({
    predicate: (action) => (action as AnyAction).type === 'inbox/message.received',
    effect: (action, listenerApi) => {
      const payload = (action as AnyAction & { payload?: MessageReceivedPayload }).payload;
      if (!payload?.conversationId || !payload.message) return;

      // Só mensagem do cliente conta como "nova mensagem chegada".
      if (payload.message.direction !== 'INBOUND') return;

      // listenerApi.getState() é `unknown` (state genérico p/ evitar ciclo). Cast.
      const state = listenerApi.getState() as Parameters<typeof selectCurrentUser>[0] &
        Parameters<typeof selectSelectedConversationId>[0];

      // Badge "Conversas" reflete só as conversas atribuídas a mim (scope mine).
      const myId = selectCurrentUser(state)?.id ?? null;
      if (!myId || payload.assignedToId !== myId) return;

      const d = listenerApi.dispatch as LooseDispatch;

      // Conversa aberta na hora = sendo lida: zera local + confirma no backend
      // (markRead propaga conversation.read pros outros atendentes).
      if (selectSelectedConversationId(state) === payload.conversationId) {
        d(conversationUnreadSet({ conversationId: payload.conversationId, count: 0 }));
        d(inboxApi.endpoints.markRead.initiate(payload.conversationId));
        return;
      }

      d(
        conversationUnreadSet({
          conversationId: payload.conversationId,
          count: payload.unreadCount,
        }),
      );
    },
  });

  // conversation.read → zera (sincroniza entre atendentes).
  listenerMiddleware.startListening({
    predicate: (action) => (action as AnyAction).type === 'inbox/conversation.read',
    effect: (action, listenerApi) => {
      const payload = (action as AnyAction & { payload?: ConversationReadPayload }).payload;
      if (!payload?.conversationId) return;
      (listenerApi.dispatch as LooseDispatch)(
        conversationUnreadSet({ conversationId: payload.conversationId, count: 0 }),
      );
    },
  });
}
